import { Select } from '@ardoq/select';
import { DeleteIcon } from '@ardoq/icons';
import { GhostButton, DragButton } from '@ardoq/button';
import {
  FormattingRuleGroupBox,
  GroupBox,
  InputsWrapper,
} from '../../../atoms';
import { useFormattingContext } from '../useFormattingContext';
import { t } from '@ardoq/i18n';
import { FormattingRuleLabel } from './atoms';
import {
  getSelectFieldOptions,
  RuleNotificationType,
} from '@ardoq/query-builder';
import type { ComponentOrReferenceFormattingRule } from '../types';
import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import { WarningNotificationRule } from '../../../WarningNotificationRule';
import { perspectivesRowDragAndDropHandlers } from '../../../perspectivesRowDragAndDropHandlers';
import { FormattingRuleRowByComponentOrReferenceBody } from './FormattingRuleRowByComponentOrReferenceBody';
import { FORMATTING_RULE_LINE_HEIGHT as LINE_HEIGHT } from '../../../consts';

type FormattingRuleRowByComponentOrReferenceProperties = {
  formattingRule: ComponentOrReferenceFormattingRule;
};

export const FormattingRuleRowByComponentOrReference = ({
  formattingRule: rule,
}: FormattingRuleRowByComponentOrReferenceProperties) => {
  const formattingContext = useFormattingContext();
  const {
    updateRule,
    removeRule,
    getRuleNotificationParams,
    ruleInfo,
    i18nLocale,
  } = formattingContext;

  const { __id: ruleId, field, type } = rule;
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(ruleId, useDragAndDropContext());

  const info = ruleInfo[ruleId];
  const filter = field ? info?.fieldFormatting?.[field] : null;

  if (!info || (field && !filter)) {
    const {
      message = t(
        i18nLocale,
        'perspectives:formattingRuleRow.errorCouldNotLoad',
        { field },
        'Error - could not load formatting rule "{{field}}"'
      ),
      notificationType,
    } = getRuleNotificationParams?.(rule) || {};
    const isWarning = notificationType === RuleNotificationType.WARNING;

    if (!isWarning) {
      return <GroupBox>{message}</GroupBox>;
    }

    return (
      <WarningNotificationRule
        message={message}
        removeRule={() => removeRule(ruleId)}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        dataTestId="group-box-formatting-warning-notification"
      />
    );
  }

  const { fieldFormatting, filterGroups: formattingGroups, comparators } = info;

  return (
    <FormattingRuleGroupBox
      data-testid={`group-box-formatting-rule-by-${type}`}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <DragButton
        draggable={true}
        style={{ lineHeight: `${LINE_HEIGHT}px` }}
        i18nLocale={i18nLocale}
      />
      <FormattingRuleLabel ruleType={type} />
      <InputsWrapper $align={'start'}>
        <Select
          aria-label={t(
            i18nLocale,
            'perspectives:formattingRuleRow.attribute',
            'Attribute'
          )}
          dataTestId="formatting-rule-field-select"
          value={field}
          options={getSelectFieldOptions(formattingGroups ?? [])}
          onValueChange={field => {
            if (typeof field !== 'string') return; // shouldn't happen
            const filter = fieldFormatting[field];
            if (!filter) return; // shouldn't happen

            updateRule(ruleId, {
              field,
              comparator: filter.comparators[0],
              value: null,
            });
          }}
        />
        {filter ? (
          <FormattingRuleRowByComponentOrReferenceBody
            rule={rule}
            filter={filter}
            comparators={comparators}
            formattingContext={formattingContext}
            isNewConditionalFormatting={false}
          />
        ) : (
          <>
            <div style={{ flex: 1 }}>&nbsp;</div>
            <div style={{ flex: 1 }}>&nbsp;</div>
            <div style={{ flex: 1 }}>&nbsp;</div>
            <div style={{ flex: 1 }}>&nbsp;</div>
          </>
        )}
      </InputsWrapper>
      <GhostButton
        dataTestId="remove-formatting-rule-button"
        onClick={() => removeRule(ruleId)}
      >
        {filter && <DeleteIcon />}
      </GhostButton>
    </FormattingRuleGroupBox>
  );
};
